import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { takeUntil } from 'rxjs/operators';
import { KpisService } from 'src/app/component/kpis/kpis-service/kpis.service';
import * as moment from 'moment-timezone';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-statistics-modal',
  templateUrl: './statistics-modal.component.html',
  styleUrls: ['./statistics-modal.component.css']
})
export class StatisticsModalComponent implements OnInit, OnDestroy {
  @Output() onCloseModal = new EventEmitter<any>();
  selectedChartType: 'pie' | 'radar' | 'polarArea' = 'pie';
  drivers: any[] = [];
  revenues: any;
  fundingSources: any[] = [];
  totalJobs = 0;
  totalCancelledTrips = 0;
  totalCompletedTrips = 0;
  view = 'graph';
  driverPieChartData: any = { labels: [], datasets: [] };
  driverBarChartData: any = { labels: [], datasets: [] };
  fsBarChartData: any = { labels: [], datasets: [] };
  revenueBarChartData: any = { labels: [], datasets: [] };
  driverPolarChartData: any = { labels: [], datasets: [] };
  fundingSourceDonutData: any = { labels: [], datasets: [] };
  fundingSourceDonutOptions: any = {};

  driverBarChartOptions: any = {};
  driverPolarChartOptions: any = {};

  radarChartData: any = { labels: [], datasets: [] };
  radarChartOptions: any = {};
  showFundingSourceBar = false


  barChartOptions: any;
  dateForm: UntypedFormGroup;
  chartColors = {
    completed: 'rgba(74, 222, 128, 0.6)',   // green with 60% opacity
    cancelled: 'rgba(248, 113, 113, 0.6)',  // red with 60% opacity
    pending: 'rgba(161, 163, 246, 0.6)'     // purple with 60% opacity
  };

  constructor(private kpisService: KpisService, private fb: UntypedFormBuilder) { }

  ngOnInit() {
    this.dateForm = this.fb.group({
      dateRange: [[moment().startOf('day').toISOString(), moment().endOf('day').toISOString()]]
    });
    this.getNemtStatsByDateRange();
  }

  calculateTotals(fundingSources) {
    this.totalJobs = fundingSources.reduce((acc, item) => acc + item.totalJobs, 0);
    this.totalCancelledTrips = fundingSources.reduce((acc, item) => acc + item.totalCancelledJobs, 0);
    this.totalCompletedTrips = fundingSources.reduce((acc, item) => acc + item.totalCompletedJobs, 0);
  }

  buildPieChart() {
    this.driverPieChartData = {
      labels: ['Completed', 'Cancelled', 'Remaining'],
      datasets: [
        {
          data: [
            this.totalCompletedTrips,
            this.totalCancelledTrips,
            this.totalJobs - (this.totalCompletedTrips + this.totalCancelledTrips)
          ],
          backgroundColor: [
            this.chartColors.completed,
            this.chartColors.cancelled,
            this.chartColors.pending
          ],
          hoverBackgroundColor: [
            this.chartColors.completed,
            this.chartColors.cancelled,
            this.chartColors.pending
          ]
        }
      ]
    };
  }

  buildPolarAreaChart() {

    this.driverPolarChartData = {
      labels: ['Completed', 'Cancelled', 'Remaining'],
      datasets: [
        {
          label: 'Trips',
          data: [this.totalCompletedTrips, this.totalCancelledTrips, this.totalJobs - (this.totalCompletedTrips + this.totalCancelledTrips)],
          backgroundColor: [
            this.chartColors.completed,
            this.chartColors.cancelled,
            this.chartColors.pending
          ],
          hoverBackgroundColor: [
            this.chartColors.completed,
            this.chartColors.cancelled,
            this.chartColors.pending
          ],
          borderColor: [
            this.chartColors.completed,
            this.chartColors.cancelled,
            this.chartColors.pending
          ],
          borderWidth: 1
        }
      ]
    };
    this.driverPolarChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: 'bottom'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const dataset = context.dataset;
            const value = dataset.data[context.dataIndex];
            const total = dataset.data.reduce((a, b) => a + b, 0);
            const pct = total ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${pct}%)`;
          }
        }
      },
      scales: {
        r: {
          ticks: {
            beginAtZero: true,
            display: false
          }
        }
      }
    };


  }

  buildDriverBarChart() {
    this.driverBarChartData = {
      labels: this.drivers.map(d => d.driver.displayName.length > 12
        ? d.driver.displayName.substring(0, 12) + '…'
        : d.driver.displayName),
      datasets: [
        { label: 'Completed', data: this.drivers.map(d => d.totalCompletedJobs), backgroundColor: this.chartColors.completed },
        { label: 'Cancelled', data: this.drivers.map(d => d.totalCancelledJobs), backgroundColor: this.chartColors.cancelled }
      ]
    }

    this.driverBarChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        xAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
        yAxes: [{ stacked: true }]
      },
      legend: { position: 'bottom' },
      tooltips: {
        mode: 'index',          // <- show all datasets for the hovered index
        intersect: false,       // <- don't require direct hit
        displayColors: true,    // <- color box per dataset (default true)
        callbacks: {
          title: (items, data) => {
            const idx = items[0].index;
            return this.drivers[idx].driver.displayName; // full name
          },
          // One line per dataset; color boxes will match each dataset
          label: (item, data) => {
            const ds = data.datasets[item.datasetIndex];
            const val = Number(ds.data[item.index]) || 0;
            // optional: add % of total
            const total = data.datasets.reduce((s, d) => s + (Number(d.data[item.index]) || 0), 0);
            const pct = total ? ` (${((val / total) * 100).toFixed(1)}%)` : '';
            return `${ds.label}: ${val}${pct}`;
          },
          // Add a Total line at the bottom
          footer: (items, data) => {
            const total = items.reduce(
              (s, it) => s + (Number(data.datasets[it.datasetIndex].data[it.index]) || 0),
              0
            );
            return `Total: ${total}`;
          }
        }
      }
    };
  }

  buildFundingSourceBarChart() {
    this.fsBarChartData = {
      labels: this.fundingSources.map(fs => fs.cooperate.displayName),
      datasets: [
        { label: 'Completed', data: this.fundingSources.map(fs => fs.totalCompletedJobs), backgroundColor: this.chartColors.completed },
        { label: 'Cancelled', data: this.fundingSources.map(fs => fs.totalCancelledJobs), backgroundColor: this.chartColors.cancelled }
      ]
    };
  }

  buildRevenueBarChart() {
    if (!this.revenues) return;

    this.revenueBarChartData = {
      labels: this.revenues.map(r => r._id.displayName),
      datasets: [
        { label: 'Completed', data: this.revenues.map(r => r.totalCompletedJobs), backgroundColor: this.chartColors.completed },
        { label: 'Cancelled', data: this.revenues.map(r => r.totalCancelledJobs), backgroundColor: this.chartColors.cancelled }
      ]
    };
  }

  setBarChartOptions() {
    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { font: { size: 12 } }
        }
      },
      scales: {
        x: { stacked: false, ticks: { font: { size: 11 } } },
        y: { beginAtZero: true, ticks: { stepSize: 1 } }
      }
    };
  }

  getNemtStatsByDateRange() {
    const [start, end] = this.dateForm.value.dateRange;
    if (!start || !end) return;

    const startDate = moment(start).startOf('day').toISOString();
    const endDate = moment(end).endOf('day').toISOString();

    this.kpisService.getNemtReportsByDate(startDate, endDate)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (!data) return;

        this.fundingSources = data.cooperateKpis;
        this.drivers = data.driverKpis;
        this.revenues = data.revenueKpis[0].sources;
        this.calculateTotals(this.fundingSources);
        this.buildPieChart();
        this.buildPolarAreaChart();
        this.buildDriverBarChart();
        this.buildFundingSourceBarChart();
        this.buildFundingSourceDonutChart()
        this.buildRevenueBarChart();
        this.setBarChartOptions();
        this.buildRadarChart();
      });
  }

  buildFundingSourceDonutChart() {
  if (!this.fundingSources.length) return;

  this.fundingSourceDonutData = {
    labels: this.fundingSources.map(fs => fs.cooperate.displayName),
    datasets: [
      {
        label: 'Completed',
        data: this.fundingSources.map(fs => fs.totalCompletedJobs),
        backgroundColor: this.fundingSources.map(() => this.chartColors.completed),
        hoverBackgroundColor: this.fundingSources.map(() => this.chartColors.completed)
      },
      {
        label: 'Cancelled',
        data: this.fundingSources.map(fs => fs.totalCancelledJobs), // 👈 yeh field tumhare model me honi chahiye
        backgroundColor: this.fundingSources.map(() => this.chartColors.cancelled),
        hoverBackgroundColor: this.fundingSources.map(() => this.chartColors.cancelled)
      }
    ]
  };

  this.fundingSourceDonutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const dataset = context.dataset;
            const value = dataset.data[context.dataIndex];
            const total = dataset.data.reduce((a: number, b: number) => a + b, 0);
            const pct = total ? ((value / total) * 100).toFixed(1) : 0;
            return `${dataset.label} - ${context.label}: ${value} (${pct}%)`;
          }
        }
      }
    },
    cutout: '60%'
  };
}

buildRadarChart() {
  this.radarChartData = {
    labels: ['Completed', 'Cancelled', 'Remaining'],
    datasets: [
      {
        label: 'Completed',
        data: [
          this.totalCompletedTrips,0,0

        ],
        backgroundColor: this.chartColors.completed,   // blue with opacity
        borderColor:  this.chartColors.completed,         // solid blue
        pointBackgroundColor:  this.chartColors.completed,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor:  this.chartColors.completed,
      },
       {
        label: 'Cancelled',
        data: [
          0,this.totalCancelledTrips,0
        ],
        backgroundColor: this.chartColors.cancelled,   // blue with opacity
        borderColor: this.chartColors.cancelled,         // solid blue
        pointBackgroundColor: this.chartColors.cancelled,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: this.chartColors.cancelled,
      }
      ,
       {
        label: 'Remaining',
        data: [
           0,0,this.totalJobs - (this.totalCompletedTrips + this.totalCancelledTrips)
        ],
        backgroundColor: this.chartColors.pending,   // blue with opacity
        borderColor: this.chartColors.pending,         // solid blue
        pointBackgroundColor: this.chartColors.pending,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: this.chartColors.pending,
      }
    ]
  };

  this.radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom'
    },
    scales: {
      r: {
        angleLines: { display: true },
        suggestedMin: 0
      }
    },
  };
}


  ngOnDestroy(): void { }

  closeModal() {
    this.onCloseModal.emit(false);
  }
}
